import { useParams } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InternalLinks } from "@/components/InternalLinks";
import { CasinoCard } from "@/components/CasinoCard";
import { listCasinos } from "@/data/casinos";

export function BettingHubPage() {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const { topic } = useParams();
  const casinos = listCasinos();
  const baseTitle = isSv
    ? "Spelbolag och betting i Sverige 2026"
    : "Sportsbooks and betting in Sweden 2026";
  const baseDesc = isSv
    ? "Jämför spelbolag i Sverige — odds, sportsbooks, livebetting och bonusar både med och utan svensk licens."
    : "Compare sportsbooks in Sweden — odds, live betting and bonuses both with and without Swedish license.";

  const title = topic ? `${baseTitle} — ${topic.replace(/-/g, " ")}` : baseTitle;
  const desc = topic ? `${title}. ${baseDesc}` : baseDesc;

  return (
    <div className="container-page py-8">
      <SEO title={title} description={desc} />
      <Breadcrumbs items={[{ label: t("nav.home"), href: prefix }, { label: title }]} />
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{title}</h1>
      <p className="mt-2 max-w-3xl text-ink-700">{desc}</p>

      <article className="prose prose-ink mt-6 max-w-3xl [&>h2]:mt-8 [&>h2]:mb-3 [&>h2]:text-2xl [&>h2]:font-bold [&>p]:my-3">
        <h2>{isSv ? "Sportbetting med svensk licens" : "Sports betting under Swedish license"}</h2>
        <p>
          {isSv
            ? "Spelbolag som riktar sig till svenska spelare på vadhållning behöver en separat licens från Spelinspektionen. Den kombineras ofta med en casinolicens hos de stora aktörerna. Du loggar in med BankID, sätter en insättningsgräns och är ansluten till Spelpaus precis som på casinot. Bonusar är begränsade till en per spelare."
            : "Operators offering sports betting to Swedish players require a separate license from Spelinspektionen. The big operators often hold it alongside their casino license. You log in with BankID, set a deposit limit and are connected to Spelpaus, just like on casino. Bonuses are limited to one per player."}
        </p>
        <h2>{isSv ? "Populära sporter" : "Popular sports"}</h2>
        <p>
          {isSv
            ? "Fotboll dominerar — Premier League, Champions League och Allsvenskan är de största marknaderna. Hockey är den näst största sporten, med SHL och NHL i topp. Tennis, basket, mma och esport är resten av de högvolym-disciplinerna. Esport-betting på CS2, Dota 2 och Valorant växer snabbt."
            : "Football dominates — Premier League, Champions League and Allsvenskan are the largest markets. Hockey is second, with the SHL and the NHL on top. Tennis, basketball, MMA and esports make up the remaining high-volume disciplines. Esports betting on CS2, Dota 2 and Valorant is growing fast."}
        </p>
        <h2>{isSv ? "Vanliga marknader" : "Common markets"}</h2>
        <p>
          {isSv
            ? "Match Winner är grunden. Asian Handicap och Over/Under på mål eller poäng är populära för fotboll, hockey och basket. Player Props (mål, kort, assist) är vanliga i fotboll. Live-betting har blivit den största enskilda kategorin volymmässigt — odds rör sig snabbt under matchen och kräver att du följer spelet i realtid."
            : "Match Winner is the foundation. Asian Handicap and Over/Under on goals or points are popular for football, hockey and basketball. Player props (goals, cards, assists) are common in football. Live betting has become the single largest category by volume — odds shift fast during the match and you need to follow the game live."}
        </p>
      </article>

      <h2 className="mt-10 text-2xl font-bold">
        {isSv ? "Spelbolag att jämföra" : "Sportsbooks to compare"}
      </h2>
      <div className="mt-4 grid gap-4">
        {casinos.map((c, i) => (
          <CasinoCard key={c.id} casino={c} rank={i + 1} source="betting-hub" />
        ))}
      </div>

      <InternalLinks context="betting" />
    </div>
  );
}
